import {
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useContext, useEffect, useRef } from "react";
import { endpoints } from "../../Utils/UrlEnum";
import { useForm, useWatch, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { SubscriptionFormProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import Config from "../../Utils/Config";
import { SubscriptionFormValidationSchema } from "../../Utils/Models";
import { onFormError } from "../../Utils/Utils";
import { CRUDContext } from "./SubscriptionsTable";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import styles from "../../Styles/SubscriptionForm.module.css";

export default function SubscriptionForm(props: SubscriptionFormProps) {
  const subscriptionContext: any = useContext(CRUDContext);
  const ref: any = useRef();
  const firstUpdate = useRef(true);

  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    formState,
    reset,
  } = useForm({
    resolver: yupResolver(SubscriptionFormValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      durationInDays: 0,
      isActive: true,
      benefits: [],
    },
  });

  const formData = useWatch({ control: control });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "benefits",
  });

  /**
   * Update context with form data on change
   */
  useEffect(() => {
    if (formData && !firstUpdate.current) {
      // Propagate form changes to parent component if needed
    }
  }, [formData]);

  /**
   * Initialize form with existing subscription data
   */
  useEffect(() => {
    if (
      subscriptionContext &&
      subscriptionContext.name &&
      subscriptionContext.name !== ""
    ) {
      reset(subscriptionContext);
    }
  }, [subscriptionContext, reset]);

  /**
   * Auto-submit on form value changes
   */
  useEffect(
    () => {
      if (firstUpdate.current) {
        firstUpdate.current = false;
        return;
      }
      if (!firstUpdate.current) {
        ref.current?.submit();
      }
    },
    [
      // props.shouldHandleSubmit
    ]
  );

  /**
   * Update existing subscription
   */
  const updateElement = async (model: any) => {
    const url = `${endpoints.subscriptions.main}/${subscriptionContext?._id}`;
    const res = (await updateData(url, model)) as any;
    if (!res || res?.error) {
      return;
    }
    props.onClose();
  };

  /**
   * Create new subscription
   */
  const createElement = async (model: any) => {
    const url = `${endpoints.subscriptions.main}`;
    const res = (await postData(url, model)) as any;
    if (!res || res?.error) {
      return;
    }
    props.onClose();
  };

  /**
   * Handle form submission
   */
  const onSubmit = (model: any) => {
    subscriptionContext?._id ? updateElement(model) : createElement(model);
  };

  /**
   * Add new benefit
   */
  const handleAddBenefit = () => {
    append({ name: "" });
  };

  /**
   * Remove benefit
   */
  const handleRemoveBenefit = (index: number) => {
    remove(index);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      ref={ref}
      id={FormsIds.subscriptionForm}
    >
      <div className={styles.formContainer}>
        {/* SECTION: Subscription Details */}
        <div className={styles.sectionContainer}>
          <div className={styles.sectionTitle}>
            {Vocabulary.subscriptionDetails}
          </div>

          <Grid container spacing={Config.standardGridSpacing}>
            {/* Subscription Name */}
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                {...register("name")}
                id="name"
                name="name"
                label={Vocabulary.name}
                value={getValues("name") || ""}
                fullWidth
                variant="outlined"
                size="small"
                error={typeof formState.errors.name === "object"}
                helperText={formState.errors.name?.message?.toString()}
              />
            </Grid>

            {/* Price Input */}
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                {...register("price")}
                id="price"
                name="price"
                label={`${Vocabulary.price} (RON)`}
                value={getValues("price") || ""}
                fullWidth
                variant="outlined"
                type="number"
                size="small"
                inputProps={{ step: "0.01", min: "0" }}
                error={typeof formState.errors.price === "object"}
                helperText={formState.errors.price?.message?.toString()}
              />
            </Grid>

            {/* Duration Input */}
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                {...register("durationInDays")}
                id="durationInDays"
                name="durationInDays"
                label={`${Vocabulary.duration} (zile)`}
                value={getValues("durationInDays") || ""}
                fullWidth
                variant="outlined"
                type="number"
                size="small"
                inputProps={{ step: "1", min: "0" }}
                error={typeof formState.errors.durationInDays === "object"}
                helperText={formState.errors.durationInDays?.message?.toString()}
              />
            </Grid>

            {/* Status Switch */}
            <Grid item xs={12} sm={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    {...register("isActive")}
                    checked={watch("isActive") || false}
                    name="isActive"
                    id="isActive"
                  />
                }
                label={Vocabulary.active}
              />
            </Grid>
          </Grid>
        </div>

        {/* SECTION: Subscription Benefits */}
        <div className={styles.sectionContainer}>
          <div className={styles.sectionTitle}>
            {Vocabulary.subscriptionBenefits}
          </div>

          {/* Add Benefit Button - at top */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBenefit}
            className={styles.addBenefitButton}
            size="small"
          >
            {Vocabulary.addBenefit}
          </Button>

          <div className={styles.benefitsContainer}>
            {fields.length > 0 ? (
              <div className={styles.benefitsWrapper}>
                {fields.map((field, index) => (
                  <div key={field.id} className={styles.benefitItemContainer}>
                    <div className={styles.benefitItem}>
                      {/* Benefit Text Field */}
                      <TextField
                        {...register(`benefits.${index}.name`)}
                        id={`benefits-name-${index}`}
                        name={`benefits.${index}.name`}
                        label={Vocabulary.text}
                        value={getValues(`benefits.${index}.name`) || ""}
                        fullWidth
                        variant="outlined"
                        size="small"
                        multiline
                        error={!!formState.errors.benefits?.[index]?.name}
                        helperText={
                          formState.errors.benefits?.[index]?.name?.message?.toString() ||
                          ""
                        }
                      />

                      {/* Delete Button */}
                      <IconButton
                        onClick={() => handleRemoveBenefit(index)}
                        color="error"
                        size="small"
                        title={Vocabulary.removeBenefit}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyBenefitsMessage}>
                {"Nu sunt beneficii adăugate. Faceți clic pe butonul de mai sus pentru a adăuga."}
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
